/**
 * Purpose:
 *   Menu item component - composes simple and expandable menu items.
 *
 * Notes:
 *   - Delegates to SimpleMenuItem or ExpandableMenuItem based on item type
 *   - ICONS STAY IN FIXED POSITION - never move during transitions
 *   - Only text and chevron animate (fade out)
 */

'use client';

import React from 'react';
import { useMenuItem } from './(hooks)/useMenuItem';
import { SimpleMenuItem } from './SimpleMenuItem';
import { ExpandableMenuItem } from './ExpandableMenuItem';
import type { MenuItemConfig } from '@/types/menu';

export interface IMenuItemProps {
  item: MenuItemConfig;
  isExpanded: boolean;
  isActive: boolean;
  isParentActive: boolean;
  onToggle: () => void;
  onNavigate: (href: string) => void;
  isChildActive: (item: MenuItemConfig) => boolean;
  onLinkClick?: () => void;
  isCollapsed?: boolean;
}

export const MenuItem: React.FC<IMenuItemProps> = ({
  item,
  isExpanded,
  isActive,
  isParentActive,
  onToggle,
  onNavigate,
  isChildActive,
  onLinkClick,
  isCollapsed = false,
}) => {
  const { hasChildren } = useMenuItem({
    item,
    isExpanded,
    isActive,
    isParentActive,
    isCollapsed,
  });

  if (hasChildren) {
    return (
      <ExpandableMenuItem
        item={item}
        isExpanded={isExpanded}
        isActive={isActive}
        isParentActive={isParentActive}
        onToggle={onToggle}
        onNavigate={onNavigate}
        isChildActive={isChildActive}
        onLinkClick={onLinkClick}
        isCollapsed={isCollapsed}
      />
    );
  }

  return (
    <SimpleMenuItem
      item={item}
      isActive={isActive}
      isCollapsed={isCollapsed}
      onNavigate={onNavigate}
      onLinkClick={onLinkClick}
    />
  );
};
